import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import { apiFetch } from '../../lib/api';
import UrgentFollowupsPanel from '../../components/UrgentFollowupsPanel';
import { useAuth } from '../../components/LoginGate';
import { canSeeFinance } from '../../lib/constants';

type Range = 'all' | 'month' | '6months' | 'annual' | 'custom';

interface CaseTypeEntry { type: string | null; month: string | null; count: number; }

// Financial fields are optional — the server strips them for staff role.
// Always guard reads with `canSeeFinance(user?.role)` OR a nullish-coalesce.
interface DashboardData {
  total_clients: number;
  total_cases: number;
  active_cases: number;
  closed_cases: number;
  total_revenue?: number;
  total_collected?: number;
  outstanding?: number;
  monthly_revenue: { month: string; revenue?: number; collected?: number }[];
  team_performance: { name: string; target?: number; achieved?: number; ratio?: number }[];
  case_type_counts: CaseTypeEntry[];
  status_counts: { status: string; count: number }[];
  recent_activity: { id: number; entity_type: string; entity_id: number; action: string; details: string | null; created_at: string }[];
  upcoming_deadlines: { id: number; client_name: string; file_reference: string; case_type: string; team_name: string; priority: string; next_deadline_date: string; file_status: string }[];
  range: string;
}

function formatCurrency(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function relativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
}

function daysUntil(dateStr: string | null): number {
  if (!dateStr) return 999;
  const now = new Date(); now.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target.getTime() - now.getTime()) / 86400000);
}

function progressColor(pct: number): string {
  if (pct >= 100) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
}

function statusBadgeClass(status: string | null): string {
  if (!status) return 'badge badge-gray';
  const s = status.toLowerCase();
  if (s === 'active' || s === 'open') return 'badge badge-green';
  if (s === 'closed' || s === 'completed') return 'badge badge-gray';
  if (s === 'pending') return 'badge badge-yellow';
  return 'badge badge-blue';
}

function monthLabel(m: string): string {
  return new Date(m + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: 'month', label: 'This Month' },
  { value: '6months', label: '6 Months' },
  { value: 'annual', label: 'Year to Date' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

export default function DashboardPage() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const showFinance = canSeeFinance(user?.role);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchData = useCallback((r: Range, from?: string, to?: string) => {
    setLoading(true);
    const params = new URLSearchParams({ range: r });
    if (r === 'custom' && from && to) { params.set('from', from); params.set('to', to); }
    apiFetch(`/api/dashboard?${params}`)
      .then(res => { if (!res.ok) throw new Error('error'); return res.json(); })
      .then((d: DashboardData) => {
        setData({
          ...d,
          monthly_revenue: d.monthly_revenue ?? [],
          team_performance: d.team_performance ?? [],
          case_type_counts: d.case_type_counts ?? [],
          status_counts: d.status_counts ?? [],
          recent_activity: d.recent_activity ?? [],
          upcoming_deadlines: d.upcoming_deadlines ?? [],
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (range !== 'custom') fetchData(range);
  }, [range, fetchData]);

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (!data && loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center' }}>Failed to load dashboard.</div>;

  const collectionRate = (data.total_revenue ?? 0) > 0
    ? (((data.total_collected ?? 0) / (data.total_revenue ?? 1)) * 100).toFixed(1)
    : '0.0';

  const maxMonthlyValue = Math.max(
    ...data.monthly_revenue.flatMap(m => [m.revenue ?? 0, m.collected ?? 0]),
    1,
  );

  const isMonthlyBreakdown = range === '6months' || range === 'annual' || (range === 'custom' && customFrom && customTo);

  let caseTypeMonths: string[] = [];
  let caseTypeTypes: string[] = [];
  let caseTypeMatrix: Record<string, Record<string, number>> = {};

  if (isMonthlyBreakdown) {
    const monthsSet = new Set<string>();
    const typesSet = new Set<string>();
    data.case_type_counts.forEach(e => { if (e.month) monthsSet.add(e.month); if (e.type) typesSet.add(e.type); });
    caseTypeMonths = Array.from(monthsSet).sort();
    caseTypeTypes = Array.from(typesSet);
    data.case_type_counts.forEach(e => {
      if (!e.type || !e.month) return;
      if (!caseTypeMatrix[e.type]) caseTypeMatrix[e.type] = {};
      caseTypeMatrix[e.type][e.month] = e.count;
    });
  } else {
    const totals: Record<string, number> = {};
    data.case_type_counts.forEach(e => { const t = e.type || 'Unspecified'; totals[t] = (totals[t] || 0) + e.count; });
    caseTypeTypes = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
    caseTypeMatrix = Object.fromEntries(caseTypeTypes.map(t => [t, { _total: totals[t] }]));
  }
  const maxSimpleCount = Math.max(...caseTypeTypes.map(t => caseTypeMatrix[t]?.['_total'] || 0), 1);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{today}</p>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'var(--bg-light)', borderRadius: 8, padding: 4 }}>
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRange(opt.value)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13,
                fontWeight: range === opt.value ? 600 : 400,
                background: range === opt.value ? '#fff' : 'transparent',
                color: range === opt.value ? 'var(--primary)' : 'var(--text-secondary)',
                boxShadow: range === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date pickers */}
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20, background: 'var(--bg-light)', borderRadius: 8, padding: '12px 16px' }}>
          <div>
            <label className="form-label">From</label>
            <input className="form-input" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ width: 160 }} />
          </div>
          <div>
            <label className="form-label">To</label>
            <input className="form-input" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ width: 160 }} />
          </div>
          <button
            className="btn btn-primary"
            onClick={() => { if (customFrom && customTo) fetchData('custom', customFrom, customTo); }}
            disabled={!customFrom || !customTo}
          >
            Apply
          </button>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '8px 0', color: 'var(--text-muted)', fontSize: 13 }}>Refreshing...</div>}

      <UrgentFollowupsPanel onOpenCase={() => navigate('/cases')} />

      {/* Summary Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Clients</div>
          <div className="stat-value">{(data.total_clients ?? 0).toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Cases</div>
          <div className="stat-value">{(data.total_cases ?? 0).toLocaleString()}</div>
          <div className="stat-sub">{data.active_cases ?? 0} active · {data.closed_cases ?? 0} closed</div>
        </div>
        {showFinance && (
          <>
            <div className="stat-card">
              <div className="stat-label">Revenue Generated</div>
              <div className="stat-value">{formatCurrency(data.total_revenue)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Amount Collected</div>
              <div className="stat-value">{formatCurrency(data.total_collected)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Outstanding Balance</div>
              <div className="stat-value" style={{ color: (data.outstanding ?? 0) > 0 ? 'var(--danger)' : undefined }}>
                {formatCurrency(data.outstanding)}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Collection Rate</div>
              <div className="stat-value">{collectionRate}%</div>
            </div>
          </>
        )}
      </div>

      {/* Revenue Chart + Lawyer Performance — paired to reduce scroll (finance-only) */}
      {showFinance && (
        <div className="grid-2" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-header"><h2 className="card-title">Revenue vs Collections</h2></div>
            {data.monthly_revenue.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>No data for selected period</div>
            ) : (
              <>
                <div className="bar-chart">
                  {data.monthly_revenue.map(m => {
                    const revH = ((m.revenue ?? 0) / maxMonthlyValue) * 100;
                    const colH = ((m.collected ?? 0) / maxMonthlyValue) * 100;
                    return (
                      <div className="bar-group" key={m.month}>
                        <div className="bar-pair">
                          <div className="bar" style={{ height: `${revH}%`, background: 'var(--primary)' }} title={`Revenue: ${formatCurrency(m.revenue)}`} />
                          <div className="bar" style={{ height: `${colH}%`, background: 'var(--success)' }} title={`Collected: ${formatCurrency(m.collected)}`} />
                        </div>
                        <div className="bar-label">{m.month ? monthLabel(m.month) : '—'}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 12, fontSize: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--primary)', display: 'inline-block' }} />Revenue
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--success)', display: 'inline-block' }} />Collected
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">Lawyer Performance</h2></div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Team</th><th>Target</th><th>Achieved</th><th>Progress</th></tr>
                </thead>
                <tbody>
                  {data.team_performance.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20 }}>No data</td></tr>}
                  {data.team_performance.map(t => (
                    <tr key={t.name}>
                      <td>{t.name}</td>
                      <td>{formatCurrency(t.target)}</td>
                      <td>{formatCurrency(t.achieved)}</td>
                      <td style={{ minWidth: 160 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="progress-bar" style={{ flex: 1 }}>
                            <div className="progress-fill" style={{ width: `${Math.min(t.ratio || 0, 100)}%`, background: progressColor(t.ratio || 0) }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: progressColor(t.ratio || 0), minWidth: 40 }}>
                            {Math.min(t.ratio || 0, 100).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Deadlines + Recent Activity — paired */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-header"><h2 className="card-title">Upcoming Deadlines</h2></div>
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Client</th><th>Type</th><th>Deadline</th><th>Days</th></tr>
              </thead>
              <tbody>
                {data.upcoming_deadlines.slice(0, 8).map(d => {
                  const days = daysUntil(d.next_deadline_date);
                  const color = days <= 7 ? 'var(--danger)' : days <= 14 ? 'var(--warning)' : 'var(--success)';
                  return (
                    <tr key={d.id}>
                      <td>{d.client_name}</td>
                      <td>{d.case_type}</td>
                      <td>{d.next_deadline_date ? new Date(d.next_deadline_date).toLocaleDateString('en-GB') : '-'}</td>
                      <td style={{ color, fontWeight: 600 }}>{days}d</td>
                    </tr>
                  );
                })}
                {data.upcoming_deadlines.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20 }}>No upcoming deadlines</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h2 className="card-title">Recent Activity</h2></div>
          <div className="timeline" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {data.recent_activity.slice(0, 10).map(a => (
              <div className="timeline-item" key={a.id}>
                <div className="timeline-title">{a.action}</div>
                <div className="timeline-date">
                  {a.details && <span>{a.details} · </span>}
                  {relativeTime(a.created_at)}
                </div>
              </div>
            ))}
            {data.recent_activity.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No recent activity</div>}
          </div>
        </div>
      </div>

      {/* Cases by Type + Status */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Cases by Type
              {isMonthlyBreakdown && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>monthly</span>}
            </h2>
          </div>
          {!isMonthlyBreakdown ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 4px' }}>
              {caseTypeTypes.map(type => {
                const count = caseTypeMatrix[type]?.['_total'] || 0;
                return (
                  <div key={type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span>{type}</span><span style={{ fontWeight: 600 }}>{count}</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${(count / maxSimpleCount) * 100}%`, background: 'var(--primary)' }} />
                    </div>
                  </div>
                );
              })}
              {caseTypeTypes.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No data</div>}
            </div>
          ) : (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              {caseTypeMonths.length === 0
                ? <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No data</div>
                : (
                  <table>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 120 }}>Case Type</th>
                        {caseTypeMonths.map(m => <th key={m} style={{ minWidth: 70, textAlign: 'center' }}>{monthLabel(m)}</th>)}
                        <th style={{ textAlign: 'center' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseTypeTypes.map(type => {
                        const rowTotal = caseTypeMonths.reduce((s, m) => s + (caseTypeMatrix[type]?.[m] || 0), 0);
                        return (
                          <tr key={type}>
                            <td style={{ fontWeight: 500, fontSize: 13 }}>{type}</td>
                            {caseTypeMonths.map(m => (
                              <td key={m} style={{ textAlign: 'center', fontSize: 13 }}>
                                {caseTypeMatrix[type]?.[m]
                                  ? <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{caseTypeMatrix[type][m]}</span>
                                  : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                              </td>
                            ))}
                            <td style={{ textAlign: 'center', fontWeight: 700 }}>{rowTotal}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header"><h2 className="card-title">Cases by Status</h2></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 4px' }}>
            {data.status_counts.map(s => (
              <div key={s.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className={statusBadgeClass(s.status)}>{s.status}</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{(s.count ?? 0).toLocaleString()}</span>
              </div>
            ))}
            {data.status_counts.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No data</div>}
          </div>
        </div>
      </div>

    </>
  );
}
